﻿import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import FormSkeleton from "@/components/FormSkeleton";

export interface ZoneQuoteFormProps {
  cityName: string;
  slug: string;
}

export default function ZoneQuoteForm({ cityName, slug }: ZoneQuoteFormProps) {
  const [agreed, setAgreed] = useState(false);
  const [urgencia, setUrgencia] = useState("esta_semana");
  const [ready, setReady] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleFormSubmit = useFormSubmit(agreed);
  const waMsg = "Hola, me interesa info sobre servicios en " + cityName;
  const waUrl = "https://wa.me/34627213347?text=" + encodeURIComponent(waMsg);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="presupuesto" className="bg-surface-alt py-16 md:py-24">
      <div className="max-w-container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          <div className="w-full lg:w-[40%]">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading font-extrabold text-3xl md:text-[40px] text-text-primary mb-4 leading-tight">
                Pídenos presupuesto en {cityName}
              </h2>
              <p className="font-body text-lg text-text-secondary mb-8">
                Te respondemos en 24h sin compromiso. Visita técnica gratuita en {cityName}.
              </p>
              <div className="flex flex-col gap-4 mb-8">
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors w-fit whitespace-nowrap"
                >
                  <span>📞</span> 951 12 07 70
                </a>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors w-fit whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i> WhatsApp 627 21 33 47
                </a>
              </div>
            </motion.div>
          </div>
          <motion.div
            className="w-full lg:w-[60%]"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm min-h-[540px]">
              {!ready ? (
                <FormSkeleton />
              ) : (
                <form
                  ref={formRef}
                  action="https://formspree.io/f/mdajpjzd"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  data-readdy-form
                  onSubmit={handleFormSubmit}
                  className="flex flex-col gap-5"
                >
                  <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <input type="hidden" name="servicio" value="Servicio completo" />

                  <div>
                    <label
                      htmlFor={`nombre-zone-${slug}`}
                      className="block font-body font-medium text-sm text-text-primary mb-1.5"
                    >
                      Nombre <span className="text-primary">*</span>
                    </label>
                    <input
                      id={`nombre-zone-${slug}`}
                      name="nombre"
                      type="text"
                      required
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`telefono-zone-${slug}`}
                      className="block font-body font-medium text-sm text-text-primary mb-1.5"
                    >
                      Teléfono <span className="text-primary">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 border border-r-0 border-border rounded-l-xl bg-slate-50 font-body text-sm text-text-secondary shrink-0">
                        +34
                      </span>
                      <input
                        id={`telefono-zone-${slug}`}
                        name="telefono"
                        type="tel"
                        required
                        className="w-full h-12 px-4 border border-border rounded-r-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="612 34 56 78"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor={`ciudad-zone-${slug}`}
                      className="block font-body font-medium text-sm text-text-primary mb-1.5"
                    >
                      Ciudad <span className="text-primary">*</span>
                    </label>
                    <input
                      id={`ciudad-zone-${slug}`}
                      name="ciudad"
                      type="text"
                      required
                      readOnly
                      value={cityName}
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-slate-50 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`urgencia-zone-${slug}`}
                      className="block font-body font-medium text-sm text-text-primary mb-1.5"
                    >
                      Urgencia <span className="text-primary">*</span>
                    </label>
                    <select
                      id={`urgencia-zone-${slug}`}
                      name="urgencia"
                      required
                      value={urgencia}
                      onChange={(e) => setUrgencia(e.target.value)}
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none cursor-pointer"
                    >
                      <option value="urgente">Urgente (hoy/mañana)</option>
                      <option value="esta_semana">Esta semana</option>
                      <option value="sin_prisa">Sin prisa (planificado)</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor={`mensaje-zone-${slug}`}
                      className="block font-body font-medium text-sm text-text-primary mb-1.5"
                    >
                      Mensaje <span className="text-text-secondary">(opcional)</span>
                    </label>
                    <textarea
                      id={`mensaje-zone-${slug}`}
                      name="mensaje"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                      placeholder={`Cuéntanos qué necesitas en ${cityName}...`}
                    ></textarea>
                    <p className="mt-1 text-xs text-text-secondary font-body">
                      Máximo 500 caracteres
                    </p>
                  </div>

                  <div className="flex items-start gap-2.5">
                    <input
                      id={`privacidad-zone-${slug}`}
                      name="privacidad"
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                    />
                    <label
                      htmlFor={`privacidad-zone-${slug}`}
                      className="font-body text-sm text-text-secondary leading-relaxed"
                    >
                      He leído y acepto la{" "}
                      <Link
                        to="/politica-privacidad"
                        className="underline text-primary hover:text-primary-dark transition-colors"
                      >
                        Política de Privacidad
                      </Link>{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <p className="font-body text-xs text-text-secondary/70 mt-1 leading-relaxed">
                      Responsable: Persianas Toldos Málaga. Finalidad: responder a tu consulta y enviarte presupuesto. Legitimación: tu consentimiento. Destinatarios: no se cederán datos a terceros. Derechos: puedes ejercer tus derechos de acceso, rectificación y supresión escribiendo a info@persianas-toldos-malaga.es
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full h-14 bg-text-primary hover:bg-slate-800 text-white font-heading font-bold text-lg rounded-pill transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    📩 Recibir presupuesto en 24h <span>→</span>
                  </button>
                  <p className="text-center font-body text-xs text-text-secondary">
                    Sin compromiso · Sin spam · Respuesta garantizada en 24h
                  </p>
                  <p className="text-center font-body text-sm text-text-secondary">
                    o llámanos al{" "}
                    <a
                      href="tel:+34951120770"
                      className="font-semibold text-primary hover:text-primary-dark underline"
                    >
                      951 12 07 70
                    </a>
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}